import { useEffect, useRef } from 'react';
import { LogoWordmark } from '@quizzach/assets';
import { LogoBox } from '@quizzach/styles';
import { RoutePath } from '@quizzach/interfaces';

// Elements that count as "not the plain page background" for the split-color
// logo. Redesigned pages tag photos, covers and dark sections with
// data-darkzone so the teal logo layer wipes over them.
const ZONES_SELECTOR = '[data-darkzone]';

/**
 * Split-color wordmark: an ink-colored layer with a teal layer on top whose
 * clip-path is revealed exactly where photos / dark zones pass behind the
 * logo. Runs continuously (marquee covers slide under the logo without any
 * scrolling), with a slow interval safety net for occluded windows where
 * requestAnimationFrame is throttled.
 */
export const SplitLogo = () => {
	const boxRef = useRef<HTMLAnchorElement | null>(null);
	const tealRef = useRef<HTMLElement | null>(null);

	useEffect(() => {
		const box = boxRef.current;
		if (!box) return;
		const teal = box.querySelector('.lg-teal') as HTMLElement | null;
		tealRef.current = teal;
		if (!teal) return;

		let rafId: number;
		let disposed = false;

		const update = () => {
			if (disposed) return;
			const lr = box.getBoundingClientRect();
			let top = Infinity;
			let bot = -Infinity;
			let hit = false;
			document.querySelectorAll(ZONES_SELECTOR).forEach((z) => {
				const r = z.getBoundingClientRect();
				if (r.width === 0 && r.height === 0) return;
				if (r.right <= lr.left || r.left >= lr.right) return;
				const a = Math.max(lr.top, r.top);
				const b = Math.min(lr.bottom, r.bottom);
				if (b > a) {
					hit = true;
					top = Math.min(top, a);
					bot = Math.max(bot, b);
				}
			});
			teal.style.clipPath = hit
				? `inset(${Math.max(0, top - lr.top)}px 0 ${Math.max(
						0,
						lr.bottom - bot
					)}px 0)`
				: 'inset(100% 0 0 0)';
		};

		const loop = () => {
			update();
			rafId = requestAnimationFrame(loop);
		};
		loop();
		const intervalId = window.setInterval(update, 150);

		return () => {
			disposed = true;
			cancelAnimationFrame(rafId);
			window.clearInterval(intervalId);
		};
	}, []);

	return (
		<LogoBox to={RoutePath.Homepage} ref={boxRef} aria-label='Quizzach'>
			<LogoWordmark className='lg-ink' />
			<LogoWordmark className='lg-teal' />
		</LogoBox>
	);
};
